import React, { useMemo, useState } from 'react';
import { BarChart3, LineChart, PieChart, TrendingUp, TrendingDown, Activity } from 'lucide-react';

interface SmartGridChartViewProps {
  data: any[][];
  onCellChange: (row: number, col: number, value: string) => void;
  demoMode?: boolean;
}

interface ChartData {
  labels: string[];
  values: number[];
  colors: string[];
}

interface MetricCard {
  title: string;
  value: string;
  change: number;
  icon: React.ReactNode;
  color: string;
}

export const SmartGridChartView: React.FC<SmartGridChartViewProps> = ({ data, onCellChange, demoMode = false }) => {
  const [chartType, setChartType] = useState<'bar' | 'line' | 'pie'>('bar');
  const [selectedRange, setSelectedRange] = useState<{startRow: number, endRow: number, startCol: number, endCol: number} | null>(null);

  const chartData = useMemo(() => {
    const numericData: ChartData = {
      labels: [],
      values: [],
      colors: ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', 'var(--tyler-blue)', '#06b6d4', '#f97316', '#84cc16']
    };

    const textData: string[] = [];
    const allValues: number[] = [];

    let processedData: any[][] = [];
    
    if (!data || !Array.isArray(data)) {
      return { numericData, textData, allValues };
    }
    
    if (data.length > 0 && typeof data[0] === 'object' && !Array.isArray(data[0])) {
      const headers = data.length > 0 ? Object.keys(data[0]) : [];
      processedData = [headers, ...data.map(obj => headers.map(key => obj[key] || ''))];
    } else if (data.length > 0 && Array.isArray(data[0])) {
      processedData = data;
    } else {
      processedData = data.map(item => Array.isArray(item) ? item : [item]);
    }

    processedData.forEach((row, rowIndex) => {
      if (!Array.isArray(row)) {
        const cellValue = String(row || '');
        if (cellValue.trim()) {
          const numValue = parseFloat(cellValue);
          if (!isNaN(numValue)) {
            numericData.labels.push(`R${rowIndex + 1}`);
            numericData.values.push(numValue);
            allValues.push(numValue);
          } else {
            textData.push(cellValue);
          }
        }
        return;
      }
      
      row.forEach((cell, colIndex) => {
        if (cell !== null && cell !== undefined) {
          const cellStr = cell.toString().trim();
          if (cellStr) {
            const numValue = parseFloat(cellStr);
            if (!isNaN(numValue)) {
              numericData.labels.push(`R${rowIndex + 1}C${colIndex + 1}`);
              numericData.values.push(numValue);
              allValues.push(numValue);
            } else {
              textData.push(cellStr);
            }
          }
        }
      });
    });

    return { numericData, textData, allValues };
  }, [data]);

  const metrics = useMemo(() => {
    const { allValues, textData } = chartData;
    const total = allValues.reduce((sum, val) => sum + val, 0);
    const avg = allValues.length > 0 ? total / allValues.length : 0;
    const max = allValues.length > 0 ? Math.max(...allValues) : 0;
    const min = allValues.length > 0 ? Math.min(...allValues) : 0;

    const cards: MetricCard[] = [
      {
        title: 'Total Sum',
        value: total.toFixed(2),
        change: 5.2,
        icon: <TrendingUp className="w-5 h-5" />,
        color: 'blue'
      },
      {
        title: 'Average',
        value: avg.toFixed(2),
        change: -2.1,
        icon: <Activity className="w-5 h-5" />,
        color: 'green'
      },
      {
        title: 'Maximum',
        value: max.toFixed(2),
        change: 8.7,
        icon: <TrendingUp className="w-5 h-5" />,
        color: 'purple'
      },
      {
        title: 'Data Points',
        value: allValues.length.toString(),
        change: 0,
        icon: <BarChart3 className="w-5 h-5" />,
        color: 'orange'
      }
    ];

    return cards;
  }, [chartData]);

  const renderBarChart = () => {
    const { numericData } = chartData;
    const maxValue = Math.max(...numericData.values);

    return (
      <div className="bg-gray-800 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-white mb-4">Bar Chart</h3>
        <div className="flex items-end space-x-2 h-64">
          {numericData.values.slice(0, 20).map((value, index) => {
            const height = maxValue > 0 ? (value / maxValue) * 100 : 0;
            const color = numericData.colors[index % numericData.colors.length];
            
            return (
              <div key={index} className="flex-1 flex flex-col items-center">
                <div className="text-xs text-white mb-1">{value}</div>
                <div 
                  className="w-full rounded-t transition-all duration-300 hover:opacity-80 cursor-pointer"
                  style={{ 
                    height: `${height}%`,
                    backgroundColor: color,
                    minHeight: '4px'
                  }}
                  title={`${numericData.labels[index]}: ${value}`}
                  onClick={() => {
                    const [row, col] = numericData.labels[index].match(/\d+/g)?.map(n => parseInt(n) - 1) || [0, 0];
                    const newValue = prompt(`Update value for ${numericData.labels[index]}:`, value.toString());
                    if (newValue !== null) {
                      onCellChange(row, col, newValue);
                    }
                  }}
                />
                <span className="text-xs text-gray-400 mt-2 transform -rotate-45 origin-left">
                  {numericData.labels[index]}
                </span>
              </div>
            );
          })}
        </div>
        {numericData.values.length > 20 && (
          <p className="text-gray-400 text-sm mt-2">
            Showing first 20 of {numericData.values.length} data points
          </p>
        )}
      </div>
    );
  };

  const renderLineChart = () => {
    const { numericData } = chartData;
    const maxValue = Math.max(...numericData.values);
    const minValue = Math.min(...numericData.values);
    const range = maxValue - minValue;

    return (
      <div className="bg-gray-800 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-white mb-4">Line Chart</h3>
        <div className="relative h-64">
          <svg className="w-full h-full">
            {numericData.values.slice(0, 20).map((value, index) => {
              const x = (index / Math.max(numericData.values.slice(0, 20).length - 1, 1)) * 100;
              const y = range > 0 ? ((maxValue - value) / range) * 80 + 10 : 50;
              const nextValue = numericData.values[index + 1];
              
              if (index < numericData.values.slice(0, 20).length - 1 && nextValue !== undefined) {
                const nextX = ((index + 1) / Math.max(numericData.values.slice(0, 20).length - 1, 1)) * 100;
                const nextY = range > 0 ? ((maxValue - nextValue) / range) * 80 + 10 : 50;
                
                return (
                  <g key={index}>
                    <line
                      x1={`${x}%`}
                      y1={`${y}%`}
                      x2={`${nextX}%`}
                      y2={`${nextY}%`}
                      stroke="#3b82f6"
                      strokeWidth="2"
                    />
                    <circle
                      cx={`${x}%`}
                      cy={`${y}%`}
                      r="4"
                      fill="#3b82f6"
                      className="cursor-pointer hover:fill-blue-400"
                      onClick={() => {
                        const [row, col] = numericData.labels[index].match(/\d+/g)?.map(n => parseInt(n) - 1) || [0, 0];
                        const newValue = prompt(`Update value for ${numericData.labels[index]}:`, value.toString());
                        if (newValue !== null) {
                          onCellChange(row, col, newValue);
                        }
                      }}
                    >
                      <title>{`${numericData.labels[index]}: ${value}`}</title>
                    </circle>
                  </g>
                );
              }
              
              return (
                <circle
                  key={index}
                  cx={`${x}%`}
                  cy={`${y}%`}
                  r="4"
                  fill="#3b82f6"
                  className="cursor-pointer hover:fill-blue-400"
                  onClick={() => {
                    const [row, col] = numericData.labels[index].match(/\d+/g)?.map(n => parseInt(n) - 1) || [0, 0];
                    const newValue = prompt(`Update value for ${numericData.labels[index]}:`, value.toString());
                    if (newValue !== null) {
                      onCellChange(row, col, newValue);
                    }
                  }}
                >
                  <title>{`${numericData.labels[index]}: ${value}`}</title>
                </circle>
              );
            })}
          </svg>
        </div>
      </div>
    );
  };

  const renderPieChart = () => {
    const { numericData } = chartData;
    const total = numericData.values.reduce((sum, val) => sum + val, 0);
    let currentAngle = 0;

    return (
      <div className="bg-gray-800 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-white mb-4">Pie Chart</h3>
        <div className="flex items-center justify-center">
          <svg width="300" height="300" className="transform -rotate-90">
            {numericData.values.slice(0, 8).map((value, index) => {
              const percentage = total > 0 ? value / total : 0;
              const angle = percentage * 360;
              const color = numericData.colors[index % numericData.colors.length];
              
              const startAngle = currentAngle;
              const endAngle = currentAngle + angle;
              currentAngle += angle;
              
              const startAngleRad = (startAngle * Math.PI) / 180;
              const endAngleRad = (endAngle * Math.PI) / 180;
              
              const largeArcFlag = angle > 180 ? 1 : 0;
              const x1 = 150 + 100 * Math.cos(startAngleRad);
              const y1 = 150 + 100 * Math.sin(startAngleRad);
              const x2 = 150 + 100 * Math.cos(endAngleRad);
              const y2 = 150 + 100 * Math.sin(endAngleRad);
              
              const pathData = [
                `M 150 150`,
                `L ${x1} ${y1}`,
                `A 100 100 0 ${largeArcFlag} 1 ${x2} ${y2}`,
                'Z'
              ].join(' ');
              
              return (
                <path
                  key={index}
                  d={pathData}
                  fill={color}
                  className="cursor-pointer hover:opacity-80"
                  onClick={() => {
                    const [row, col] = numericData.labels[index].match(/\d+/g)?.map(n => parseInt(n) - 1) || [0, 0];
                    const newValue = prompt(`Update value for ${numericData.labels[index]}:`, value.toString());
                    if (newValue !== null) {
                      onCellChange(row, col, newValue);
                    }
                  }}
                >
                  <title>{`${numericData.labels[index]}: ${value} (${(percentage * 100).toFixed(1)}%)`}</title>
                </path>
              );
            })}
          </svg>
        </div>
        <div className="mt-4 grid grid-cols-2 gap-2">
          {numericData.values.slice(0, 8).map((value, index) => {
            const percentage = total > 0 ? (value / total) * 100 : 0;
            const color = numericData.colors[index % numericData.colors.length];
            
            return (
              <div key={index} className="flex items-center gap-2">
                <div 
                  className="w-3 h-3 rounded-full"
                  style={{ backgroundColor: color }}
                />
                <span className="text-sm text-slate-300">
                  {numericData.labels[index]}: {percentage.toFixed(1)}%
                </span>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderChart = () => {
    switch (chartType) {
      case 'line':
        return renderLineChart();
      case 'pie':
        return renderPieChart();
      default:
        return renderBarChart();
    }
  };

  return (
    <div className="h-full bg-gray-900 text-white">
      {/* Header */}
      <div className="flex justify-between items-center p-4 border-b border-gray-700">
        <h2 className="text-xl font-bold">SmartGrid Chart View</h2>
        <div className="flex items-center gap-4">
          {demoMode && <span className="text-yellow-400 text-sm">(Demo Mode)</span>}
          <div className="flex items-center gap-2">
            <button
              onClick={() => setChartType('bar')}
              className={`p-2 rounded ${chartType === 'bar' ? 'bg-[#4267A1]/60 text-white' : 'bg-slate-700 text-slate-300'}`}
              title="Bar Chart"
            >
              <BarChart3 className="w-4 h-4" />
            </button>
            <button
              onClick={() => setChartType('line')}
              className={`p-2 rounded ${chartType === 'line' ? 'bg-[#4267A1]/60 text-white' : 'bg-slate-700 text-slate-300'}`}
              title="Line Chart"
            >
              <LineChart className="w-4 h-4" />
            </button>
            <button
              onClick={() => setChartType('pie')}
              className={`p-2 rounded ${chartType === 'pie' ? 'bg-[#4267A1]/60 text-white' : 'bg-slate-700 text-slate-300'}`}
              title="Pie Chart"
            >
              <PieChart className="w-4 h-4" />
            </button>
          </div>
          <div className="text-sm text-gray-400">
            {chartData.numericData.values.length} data points
          </div>
        </div>
      </div>

      <div className="p-4 space-y-6">
        {/* Metrics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {metrics.map((metric, index) => {
            const colorClasses = {
              blue: 'bg-[#4267A1]/60',
              green: 'bg-green-600',
              purple: 'bg-[#4267A1]',
              orange: 'bg-orange-600'
            };

            return (
              <div key={index} className="bg-gray-800 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-400 text-sm font-medium">{metric.title}</p>
                    <p className="text-xl font-bold text-white mt-1">{metric.value}</p>
                    {metric.change !== 0 && (
                      <div className={`flex items-center mt-2 text-sm ${metric.change >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                        {metric.change >= 0 ? <TrendingUp className="w-3 h-3 mr-1" /> : <TrendingDown className="w-3 h-3 mr-1" />}
                        {Math.abs(metric.change)}%
                      </div>
                    )}
                  </div>
                  <div className={`p-3 rounded-full ${colorClasses[metric.color as keyof typeof colorClasses]}`}>
                    {metric.icon}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Main Chart */}
        {chartData.numericData.values.length > 0 ? (
          renderChart()
        ) : (
          <div className="bg-gray-800 rounded-lg p-12 text-center">
            <BarChart3 className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-white mb-2">No Numeric Data Found</h3>
            <p className="text-gray-400">
              Add numeric values to your SmartGrid to see charts and visualizations.
            </p>
          </div>
        )}

        {/* Data Summary */}
        <div className="bg-gray-800 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-white mb-4">Data Summary</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h4 className="text-md font-medium text-slate-300 mb-2">Numeric Values</h4>
              <div className="space-y-1 max-h-32 overflow-y-auto">
                {chartData.numericData.values.slice(0, 10).map((value, index) => (
                  <div key={index} className="flex justify-between text-sm">
                    <span className="text-gray-400">{chartData.numericData.labels[index]}</span>
                    <span className="text-white">{value}</span>
                  </div>
                ))}
                {chartData.numericData.values.length > 10 && (
                  <div className="text-gray-400 text-sm">
                    ... and {chartData.numericData.values.length - 10} more
                  </div>
                )}
              </div>
            </div>
            <div>
              <h4 className="text-md font-medium text-slate-300 mb-2">Text Entries</h4>
              <div className="space-y-1 max-h-32 overflow-y-auto">
                {chartData.textData.slice(0, 10).map((text, index) => (
                  <div key={index} className="text-sm text-gray-400 truncate">
                    {text}
                  </div>
                ))}
                {chartData.textData.length > 10 && (
                  <div className="text-gray-400 text-sm">
                    ... and {chartData.textData.length - 10} more
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
